import type { GridSelection } from './useGridSelection';

/**
 * Spreadsheet copy/paste over a {@link GridSelection}. Returns plain
 * `ClipboardEvent` handlers (no listeners registered here) so the host owns
 * lifecycle and the handlers stay unit-testable. Value coercion is injected via
 * `normalize`, keeping this agnostic of the cell's data type.
 *
 * - Copy serializes the rectangle as TSV.
 * - Paste of a single value fills the whole selection (Excel behaviour); a
 *   block anchors at the selection's top-left and clamps to the grid, skipping
 *   cells that fail to normalize.
 */
export interface GridClipboardOptions {
  selection: GridSelection;
  /** Map a column index to its persisted key. */
  colName: (ci: number) => string;
  colCount: () => number;
  rowCount: () => number;
  /** Read a cell as display text for copy ('' when blank). */
  cellText: (ri: number, ci: number) => string;
  /** Coerce pasted text; `ok: false` skips the cell. */
  normalize: (raw: string) => { ok: boolean; value: string | null };
  /** Persist the pasted edits. */
  apply: (perRow: Map<number, Record<string, string | null>>) => void;
  /** Guard: true when focus is in an input, so native copy/paste wins. */
  isBusy: () => boolean;
  /** Called with the count of cells skipped during a block paste. */
  onSkip?: (count: number) => void;
  /** Called when a single-value paste couldn't be normalized (nothing applied). */
  onEmpty?: () => void;
}

export interface GridClipboard {
  onCopy(e: ClipboardEvent): void;
  onPaste(e: ClipboardEvent): void;
}

export function useGridClipboard(opts: GridClipboardOptions): GridClipboard {
  function onCopy(e: ClipboardEvent): void {
    const s = opts.selection.selRect.value;
    if (opts.isBusy() || !s) return;
    const lines: string[] = [];
    for (let r = s.r1; r <= s.r2; r++) {
      const cells: string[] = [];
      for (let c = s.c1; c <= s.c2; c++) cells.push(opts.cellText(r, c));
      lines.push(cells.join('\t'));
    }
    e.clipboardData?.setData('text/plain', lines.join('\n'));
    e.preventDefault();
  }

  function onPaste(e: ClipboardEvent): void {
    const s = opts.selection.selRect.value;
    if (opts.isBusy() || !s) return;
    const text = e.clipboardData?.getData('text/plain');
    if (text == null) return;
    e.preventDefault();
    const grid = text
      .replace(/\r\n?/g, '\n')
      .replace(/\n$/, '')
      .split('\n')
      .map((l) => l.split('\t'));
    const perRow = new Map<number, Record<string, string | null>>();
    let skipped = 0;

    if (grid.length === 1 && grid[0]!.length === 1) {
      // Single value → fill the whole current selection.
      const norm = opts.normalize(grid[0]![0]!);
      if (!norm.ok) {
        opts.onEmpty?.();
        return;
      }
      for (let r = s.r1; r <= s.r2; r++) {
        const map: Record<string, string | null> = {};
        for (let c = s.c1; c <= s.c2; c++) map[opts.colName(c)] = norm.value;
        perRow.set(r, map);
      }
    } else {
      // Block paste → anchor at the selection's top-left, clamp to the grid.
      let maxW = 0;
      for (let dr = 0; dr < grid.length; dr++) {
        const tr = s.r1 + dr;
        if (tr >= opts.rowCount()) break;
        const cols = grid[dr]!;
        const map: Record<string, string | null> = {};
        maxW = Math.max(maxW, cols.length);
        for (let dc = 0; dc < cols.length; dc++) {
          const tc = s.c1 + dc;
          if (tc >= opts.colCount()) break;
          const norm = opts.normalize(cols[dc]!);
          if (!norm.ok) {
            skipped++;
            continue;
          }
          map[opts.colName(tc)] = norm.value;
        }
        if (Object.keys(map).length) perRow.set(tr, map);
      }
      opts.selection.setSelection(
        { ri: s.r1, ci: s.c1 },
        {
          ri: Math.min(s.r1 + grid.length - 1, opts.rowCount() - 1),
          ci: Math.min(s.c1 + maxW - 1, opts.colCount() - 1),
        },
      );
    }
    opts.apply(perRow);
    if (skipped) opts.onSkip?.(skipped);
  }

  return { onCopy, onPaste };
}
